import { Link } from "react-router-dom";
import { formatDate } from "../../../shared/utility";
import TextWithLabel from "../../controls/TextWithLabel";
import TraceManufacturedProductsTable from "./supplierItem/TraceManufacturedProductsTable";
import TracePackagedProductsTable from "./supplierItem/TracePackagedProductsTable";
import { Accordion, AccordionTab } from "primereact/accordion";
import TraceSupplierItemShippingsTable from "./supplierItem/TraceSupplierItemShippingsTable";

const viewButtonClass =
  "inline-flex items-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600";
const accordionClass = "traceability-accordion space-y-2";
const accordionHeaderClass =
  "rounded-lg border border-gray-200 bg-white hover:bg-gray-50";
const accordionContentClass = "rounded-b-lg border-x border-b border-gray-200 bg-white";

function getBarcodeLast9(barcode) {
  const text = barcode || "";
  return text.length > 9 ? text.slice(-9) : text;
}

/** New API: receiving_items is a single summary object */
function ReceivingSummaryCard({ item }) {
  const unitSuffix = item.unit_of_measure ? ` ${item.unit_of_measure}` : "";
  const receivedText =
    item.total_received_unit != null ? `${item.total_received_unit}${unitSuffix}` : "";
  const usedText =
    item.total_used_unit != null ? `${item.total_used_unit}${unitSuffix}` : "";
  const inStockText =
    item.total_in_stock_unit != null ? `${item.total_in_stock_unit}${unitSuffix}` : "";

  return (
    <div className="overflow-hidden p-4 flex border-b border-gray-200 last:border-b-0">
      <div className="grid grid-cols-4 gap-4 mt-2 flex-grow">
        <TextWithLabel label="Product Name" text={item.product_name} />
        <TextWithLabel label="Supplier Name" text={item.supplier_name} />
        <TextWithLabel label="Receiving Date" text={formatDate(item.date)} />
        <TextWithLabel label="Invoice Number" text={item.invoice_number} />
        <TextWithLabel label="Cartons Received" text={item.cartons_received} />
        <TextWithLabel label="Total Amount Received" text={receivedText} />
        <TextWithLabel label="Total Amount Used" text={usedText} />
        <TextWithLabel label="Total Amount In Stock" text={inStockText} />
      </div>
      {item.invoice_id && (
        <div className="ml-4 flex items-start">
          <Link
            className={viewButtonClass}
            to={"/receiving-invoices/" + item.invoice_id}
            target="_blank"
            rel="noopener noreferrer"
          >
            View
          </Link>
        </div>
      )}
    </div>
  );
}

/** Legacy API: receiving_items is an array of individual items */
function ReceivingItemCard({ item }) {
  return (
    <div className="overflow-hidden p-4 flex border-b border-gray-200 last:border-b-0">
      <div className="grid grid-cols-4 gap-4 mt-2 flex-grow">
        <TextWithLabel label="Product Name" text={item.product_name} />
        <TextWithLabel label="Supplier Name" text={item.supplier_name} />
        <TextWithLabel
          label="Manufacturer Name"
          text={item.manufacturer_name}
        />
        <TextWithLabel label="Product Code" text={item.plu} />
        <TextWithLabel label="Lot Number" text={item.lot_number} />
        <TextWithLabel
          label="Box Barcode"
          text={item.barcode ? getBarcodeLast9(item.barcode) : ""}
        />
        <TextWithLabel label="Box Date" text={formatDate(item.box_date)} />
        <TextWithLabel
          label="Total Unit"
          text={
            item.received_amount != null && item.unit_of_measure != null
              ? item.received_amount + item.unit_of_measure
              : ""
          }
        />
        <TextWithLabel label="Unit Cost ($)" text={item.unit_cost} />
        <TextWithLabel label="Total Cost ($)" text={item.total_cost} />
        <TextWithLabel label="Location" text={item.location} />
        <TextWithLabel label="Zone" text={item.zone} />
        <TextWithLabel label="Invoice Number" text={item.invoice_number} />
        <TextWithLabel label="Receiving Date" text={formatDate(item.date)} />
        <TextWithLabel label="Product Type" text={item.product_type} />
        <TextWithLabel label="Used Weight" text={item.used_weight} />
        <TextWithLabel label="Claims" text={item.claims} />
        <TextWithLabel label="Allergens" text={item.allergens} />
        <TextWithLabel label="Preservatives" text={item.preservatives} />
        <TextWithLabel label="Received By" text={item.received_by} />
        <TextWithLabel
          label="In Stock"
          text={item.in_stock === "1" ? "Yes" : "No"}
        />
      </div>
      {(item.invoice_id ?? item.id) && (
        <div className="ml-4 flex items-start">
          <Link
            className={viewButtonClass}
            to={"/receiving-invoices/" + (item.invoice_id ?? item.id)}
            target="_blank"
            rel="noopener noreferrer"
          >
            View
          </Link>
        </div>
      )}
    </div>
  );
}

export default function TraceSupplierItem(props) {
  const { product, lotNumber, ingredientId } = props;

  const receivingItemsRaw = product.receiving_items;
  const isSummaryObject =
    receivingItemsRaw &&
    !Array.isArray(receivingItemsRaw) &&
    typeof receivingItemsRaw === "object";
  const isLegacyArray = Array.isArray(receivingItemsRaw);
  const receivingItems = isSummaryObject
    ? [receivingItemsRaw]
    : isLegacyArray
      ? receivingItemsRaw
      : product.receiving_item
        ? [product.receiving_item]
        : [];
  const manufacturedProducts = (() => {
    if (product.manufactured_products) return product.manufactured_products;
    const grouped = product.manufactured_products_grouped;
    if (!Array.isArray(grouped)) return [];
    return grouped.map((g) => {
      const ingredients = g.ingredients_from_lot || [];
      const amountUsedRaw = ingredients.reduce(
        (sum, ing) => sum + parseFloat(ing.amount_used || 0),
        0,
      );
      const amountUsed = Math.round(amountUsedRaw * 100) / 100;
      const unitOfMeasurement =
        ingredients[0]?.unit_of_measurement_name || "Kg";
      return {
        ...g,
        amount_used: amountUsed,
        unit_of_measurement_name: unitOfMeasurement,
      };
    });
  })();
  const packagedProducts = product.packaged_products || [];
  const shippings = product.shippings || [];

  const productForManufacturedTable = {
    ...product,
    manufactured_products: manufacturedProducts,
  };

  return (
    <div className="flow-root overflow-x-auto">
      <div className="inline-block min-w-full py-2 align-middle">
        <div className="mt-2">
          <Accordion className={accordionClass} multiple activeIndex={[0]}>
            <AccordionTab
              header="Receiving History"
              headerClassName={accordionHeaderClass}
              contentClassName={accordionContentClass}
            >
              <div className="py-2">
                {receivingItems.length > 0 ? (
                  receivingItems.map((item, idx) =>
                    isSummaryObject ? (
                      <ReceivingSummaryCard
                        key={item.invoice_id || idx}
                        item={item}
                      />
                    ) : (
                      <ReceivingItemCard key={item.id || idx} item={item} />
                    ),
                  )
                ) : (
                  <div className="p-4 text-gray-500">No receiving items.</div>
                )}
              </div>
            </AccordionTab>
            <AccordionTab
              header="Manufactured Products"
              headerClassName={accordionHeaderClass}
              contentClassName={accordionContentClass}
            >
              <div className="py-2">
                <TraceManufacturedProductsTable
                  product={productForManufacturedTable}
                  lotNumber={lotNumber}
                  ingredientId={ingredientId}
                />
              </div>
            </AccordionTab>
            <AccordionTab
              header="Packaged Products"
              headerClassName={accordionHeaderClass}
              contentClassName={accordionContentClass}
            >
              <div className="py-2">
                <TracePackagedProductsTable
                  packagedProducts={packagedProducts}
                />
              </div>
            </AccordionTab>
            <AccordionTab
              header="Shipments"
              headerClassName={accordionHeaderClass}
              contentClassName={accordionContentClass}
            >
              <div className="py-2">
                <TraceSupplierItemShippingsTable shippings={shippings} />
              </div>
            </AccordionTab>
          </Accordion>
        </div>
      </div>
    </div>
  );
}
